import { yupResolver } from "@hookform/resolvers/yup";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { INFORMATION } from "../../Layout/Tabs/MdmTabPanelConstants";
import PageHeader from "../../Layout/Header/PageHeader";
import PageHeaderButton from "../../Layout/Header/PageHeaderButton";
import PageHeaderIconButton from "../../Layout/Header/PageHeaderIconButton";
import { MdmTabs } from "../../Layout/Tabs/MdmTabs";
import Carrier from "../Carrier";
import { BranchAccordion } from "./Branches/BranchAccordion";
import { carrierDefaultValues } from "./carrierDefaultValues";
import { CarrierForm } from "./CarrierForm";
import { carrierSchema } from "./carrierSchema";
import { confirm } from "../../Layout/Dialog/ConfirmDialog";
import { resetFormStateAndKeepFormValues } from "../../Forms/resetFormStateAndKeepFormValues";
import { isFormSubmitForbidden } from "../../Forms/isFormSubmitForbidden";
import { useIsUserInRoles } from "../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";

type Props = {
  title: string;
  carrier: Carrier | undefined;
  onSave: (carrier: Carrier) => void;
  onDelete?: (carrierId: number | null | undefined) => void;
  disabledFields?: Array<string> | undefined;
};

export default function CarrierFormTabs({
  title,
  carrier,
  onSave,
  onDelete,
  disabledFields,
}: Props) {
  const isUserAdminNetworkLaunch = useIsUserInRoles(
    AuthorizationPolicies.RequireAdminNetworkLaunchRoles
  );

  const methods = useForm<Carrier>({
    resolver: yupResolver(carrierSchema),
    defaultValues: carrierDefaultValues,
    mode: "all",
    disabled: !isUserAdminNetworkLaunch,
  });

  useEffect(() => {
    methods.reset(carrier);
    return () => methods.reset(carrierDefaultValues);
  }, [carrier, methods, methods.reset]);

  const onDeleteWithConfirmation = async () => {
    const isConfirmed = await confirm(
      `Do you really want to delete carrier with Id ${carrier?.Id}?`
    );
    if (!isConfirmed || !onDelete) {
      return;
    }
    await onDelete(carrier?.Id);
  };

  return (
    <>
      <PageHeader headerText={title}>
        {onDelete !== undefined && (
          <PageHeaderIconButton
            onClick={() => onDeleteWithConfirmation()}
            color="secondary"
            disabled={!isUserAdminNetworkLaunch}
          >
            <DeleteIcon />
          </PageHeaderIconButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((values: Carrier) => {
            onSave(values);
            resetFormStateAndKeepFormValues(methods);
          })}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          Save
        </PageHeaderButton>
      </PageHeader>

      <MdmTabs
        tabs={[
          {
            index: 0,
            label: INFORMATION,
            content: (
              <FormProvider {...methods}>
                <CarrierForm disabledFields={disabledFields} />
              </FormProvider>
            ),
          },
          {
            index: 1,
            label: "BRANCH",
            content: (
              <BranchAccordion
                carrierId={carrier?.Id ?? 0}
                isReadOnly={!isUserAdminNetworkLaunch}
              />
            ),
            disabled: !carrier,
          },
        ]}
      />
    </>
  );
}
